// Replaces ThemedSiderV2: pages in groups (sidebarGroups.ts), one group open at
// a time (the current page's group opens by itself), and a search box that
// shows the matching pages with their groups open; Enter opens the first match.
// Pages the admin may not list are left out, and so is a group left empty.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Drawer, Grid, Input, Layout, theme } from 'antd';
import {
  BarsOutlined,
  LeftOutlined,
  LogoutOutlined,
  RightOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  useActiveAuthProvider,
  useLogout,
  useMenu,
  useWarnAboutChange,
  type ITreeMenu,
} from '@refinedev/core';
import { useThemedLayoutContext } from '@refinedev/antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { accessControlProvider } from '@/providers/accessControlProvider';
import { SiderMenu } from './SiderMenu';
import {
  filterGroups,
  groupForRoute,
  groupMenu,
  menuSignature,
  nextOpenKey,
  openKeysFor,
  type SiderLeaf,
} from './sidebarGroups';

const WIDTH = 220;

const keyOf = (m: ITreeMenu): string => m.key ?? m.route ?? m.name;

function useListableLeaves(menuItems: ITreeMenu[]): SiderLeaf[] {
  const [allowed, setAllowed] = useState<Set<string> | null>(null);
  // useMenu returns a new array on every render, so the check reruns only when
  // the pages change (their signature); keyed on the array it looped forever.
  const routed = menuItems.filter((m) => m.route);
  const latest = useRef(routed);
  latest.current = routed;
  const signature = menuSignature(routed);
  useEffect(() => {
    let live = true;
    Promise.all(
      latest.current.map(async (m) => {
        try {
          const r = await accessControlProvider.can({
            resource: m.name,
            action: 'list',
            params: { resource: m },
          });
          return r.can ? keyOf(m) : null;
        } catch {
          return null;
        }
      })
    ).then((keys) => live && setAllowed(new Set(keys.filter((k): k is string => !!k))));
    return () => {
      live = false;
    };
  }, [signature]);
  return useMemo(
    () =>
      routed
        .filter((m) => allowed?.has(keyOf(m)))
        .map((m) => ({ key: keyOf(m), route: m.route!, label: String(m.label ?? m.name) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- routed is new each render; signature is its content
    [signature, allowed]
  );
}

export const GroupedSider: React.FC<{ Title: React.FC<{ collapsed: boolean }> }> = ({ Title }) => {
  const { token } = theme.useToken();
  const { siderCollapsed, setSiderCollapsed, mobileSiderOpen, setMobileSiderOpen } =
    useThemedLayoutContext();
  const breakpoint = Grid.useBreakpoint();
  const isMobile = typeof breakpoint.lg === 'undefined' ? false : !breakpoint.lg;
  const { menuItems, selectedKey } = useMenu();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { warnWhen, setWarnWhen } = useWarnAboutChange();
  const authProvider = useActiveAuthProvider();
  const { mutate: logout } = useLogout({
    v3LegacyAuthProviderCompatible: Boolean(authProvider?.isLegacy),
  });

  const leaves = useListableLeaves(menuItems);
  const groups = useMemo(() => groupMenu(leaves), [leaves]);
  const [query, setQuery] = useState('');
  const [openKey, setOpenKey] = useState<string | undefined>(() => groupForRoute(pathname));

  // Moving to another page opens that page's group and closes the rest.
  useEffect(() => {
    const g = groupForRoute(pathname);
    if (g) setOpenKey(g);
  }, [pathname]);

  const shown = filterGroups(groups, query);
  const openKeys = openKeysFor(shown, query, openKey);
  const collapsed = !isMobile && siderCollapsed;

  const onLogout = () => {
    if (warnWhen) {
      if (!window.confirm('Are you sure you want to leave? You have unsaved changes.')) return;
      setWarnWhen(false);
    }
    logout();
  };

  const menu = (
    <SiderMenu
      groups={shown}
      openKeys={openKeys}
      // While searching the groups stay open; otherwise one group at a time.
      onOpenChange={(keys) => !query.trim() && setOpenKey(nextOpenKey(openKeys, keys))}
      selectedKey={selectedKey}
      collapsed={collapsed}
      onClick={() => {
        setMobileSiderOpen(false);
        setQuery('');
      }}
      footer={[{ key: 'logout', icon: <LogoutOutlined />, label: 'Logout', onClick: onLogout }]}
      style={{ overflow: 'auto', flex: 1 }}
    />
  );

  const body = (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div
        style={{
          height: 64,
          flex: '0 0 64px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          padding: collapsed ? 0 : '0 16px',
          backgroundColor: token.colorBgElevated,
        }}
      >
        <Title collapsed={collapsed} />
      </div>
      {!collapsed && (
        <div style={{ padding: '8px 12px 4px' }}>
          <Input
            size="small"
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Find a page"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onPressEnter={() => {
              const first = shown[0]?.leaves[0];
              if (!first) return;
              navigate(first.route);
              setQuery('');
              setMobileSiderOpen(false);
            }}
          />
        </div>
      )}
      {menu}
    </div>
  );

  if (isMobile) {
    return (
      <>
        <Drawer
          open={mobileSiderOpen}
          onClose={() => setMobileSiderOpen(false)}
          placement="left"
          closable={false}
          width={WIDTH}
          styles={{ body: { padding: 0 } }}
        >
          <div style={{ height: '100vh', backgroundColor: token.colorBgContainer }}>{body}</div>
        </Drawer>
        <Button
          style={{
            borderStartStartRadius: 0,
            borderEndStartRadius: 0,
            position: 'fixed',
            top: 64,
            zIndex: 1001,
          }}
          size="large"
          onClick={() => setMobileSiderOpen(true)}
          icon={<BarsOutlined />}
        />
      </>
    );
  }

  return (
    <Layout.Sider
      style={{
        backgroundColor: token.colorBgContainer,
        borderRight: `1px solid ${token.colorBgElevated}`,
        height: '100vh',
        position: 'sticky',
        top: 0,
      }}
      width={WIDTH}
      collapsible
      collapsed={siderCollapsed}
      collapsedWidth={80}
      breakpoint="lg"
      onCollapse={(c, type) => type === 'clickTrigger' && setSiderCollapsed(c)}
      trigger={
        <Button
          type="text"
          style={{
            borderRadius: 0,
            height: '100%',
            width: '100%',
            backgroundColor: token.colorBgElevated,
          }}
        >
          {siderCollapsed ? (
            <RightOutlined style={{ color: token.colorPrimary }} />
          ) : (
            <LeftOutlined style={{ color: token.colorPrimary }} />
          )}
        </Button>
      }
    >
      {/* The trigger bar takes 48px at the bottom. */}
      <div style={{ height: 'calc(100vh - 48px)' }}>{body}</div>
    </Layout.Sider>
  );
};
